import type { ReactNode } from "react";
import { Icon } from "./Icon.js";
import type { IconName } from "./Icon.js";
import "./StatTile.css";

/**
 * A headline number in an icon tile, optionally linking to what it counts.
 *
 * Admin and Insights each hand-rolled a version of this independently before
 * it existed here — same shape (icon chip, big number, label, optional
 * trend), diverging just enough in markup and class names that a fix to one
 * never reached the other. One component now, so both — and anything else
 * that wants a headline number — share the same visual formula and the same
 * fix.
 */

export type StatTileTone = "default" | "warn" | "danger";

export interface StatTileProps {
  label: string;
  value: ReactNode;
  tone?: StatTileTone;
  trend?: { direction: "up" | "down"; label: string };
  icon: IconName;
  /**
   * Renders as a link to whatever list this number counts. A plain `<a
   * href>` rather than a router `Link` — this package does not depend on
   * react-router (see Breadcrumb for the same convention) — so a same-app
   * destination needs `onNavigate` to keep the router's client-side
   * transition; without it, a real navigation happens instead, which is a
   * bug only for an in-app `to` and correct for a `to` on another origin.
   */
  to?: string;
  onNavigate?: (to: string) => void;
  /** A plain action tile — "open a filter" — for when there is no route to
   *  link to at all. Ignored when `to` is set. */
  onClick?: () => void;
}

export function StatTile({ label, value, tone = "default", trend, icon, to, onNavigate, onClick }: StatTileProps) {
  const className = `ui-stat-tile ${tone !== "default" ? `is-${tone}` : ""} ${to || onClick ? "is-linked" : ""}`.trim();

  const body = (
    <>
      <div className="ui-stat-tile__icon">
        <Icon name={icon} size={16} />
      </div>
      <div className="ui-stat-tile__body">
        <span className="ui-stat-tile__value">{value}</span>
        <span className="ui-stat-tile__label">{label}</span>
      </div>
      {trend && (
        <span className={`ui-stat-tile__trend ui-stat-tile__trend--${trend.direction}`}>
          <Icon name={trend.direction === "up" ? "trend-up" : "trend-down"} size={13} />
          {trend.label}
        </span>
      )}
    </>
  );

  if (to) {
    return (
      <a
        href={to}
        className={className}
        aria-label={`${value} ${label} — open the list`}
        onClick={onNavigate ? (event) => { event.preventDefault(); onNavigate(to); } : undefined}
      >
        {body}
      </a>
    );
  }

  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick} aria-label={`${value} ${label}`}>
        {body}
      </button>
    );
  }

  return <div className={className}>{body}</div>;
}

/** Loading placeholder at the same size, so a tile grid does not jump when
 *  the real numbers land. */
export function StatTileSkeleton() {
  return (
    <div className="ui-stat-tile">
      <div className="ui-stat-tile__icon ui-stat-tile__icon--skeleton" aria-hidden="true" />
      <div className="ui-stat-tile__body">
        <span className="ui-stat-tile__value-skeleton" aria-hidden="true" />
        <span className="ui-stat-tile__label-skeleton" aria-hidden="true" />
      </div>
    </div>
  );
}
